import { useEffect } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { fmt, dateSpanish } from "@/lib/format";
import { medalLabel } from "@/lib/theme";
import type { RankingRow, AchievementPodiumRow, MedalItem } from "@/api/types";

export interface AchievementsDetailModalProps {
  user: RankingRow | null;
  onClose: () => void;
}

function isAchievementRow(row: RankingRow): row is AchievementPodiumRow {
  return "total_score" in row && "medals" in row;
}

/** Per-user achievements detail overlay. Uses framer-motion + a fullscreen
 * fixed layer because @radix-ui/react-dialog is not part of the project
 * dependency set. Closes on ESC and backdrop click, traps focus loosely. */
export function AchievementsDetailModal({ user, onClose }: AchievementsDetailModalProps) {
  const reduced = useReducedMotion();
  const open = user !== null;

  // ESC to close + lock body scroll while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && user && <Panel user={user} onClose={onClose} reduced={reduced ?? false} />}
    </AnimatePresence>
  );
}

function Panel({
  user,
  onClose,
  reduced,
}: {
  user: RankingRow;
  onClose: () => void;
  reduced: boolean;
}) {
  const achievements = isAchievementRow(user) ? user : null;
  const username = "username" in user ? user.username : "—";
  const team = "team" in user ? user.team : null;
  const rank = "rank" in user && typeof user.rank === "number" ? user.rank : null;
  const totalScore =
    achievements?.total_score ?? ("score" in user ? user.score : 0);
  const medals: MedalItem[] = achievements?.medals ?? [];
  const earned = medals.filter((m) => m.unlocked);
  const locked = medals.filter((m) => !m.unlocked);

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Logros de ${username}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reduced ? 0.1 : 0.2 }}
    >
      {/* Backdrop */}
      <motion.button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        className="absolute inset-0 bg-bg-0/75 backdrop-blur-md"
      />

      {/* Panel */}
      <motion.div
        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.97, y: 10 }}
        transition={reduced ? { duration: 0.1 } : { type: "spring", stiffness: 260, damping: 24 }}
        className={cn(
          "relative z-[61] w-full max-w-3xl rounded-[22px] border border-border-2",
          "bg-gradient-to-b from-bg-1/95 to-bg-2/90 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] backdrop-blur-xl",
          "max-h-[86vh] overflow-hidden",
        )}
      >
        {/* Glow accent */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[22px]"
          style={{
            background:
              "radial-gradient(600px 200px at 20% 0%, rgba(167,139,250,0.15), transparent 60%), radial-gradient(500px 200px at 90% 0%, rgba(236,72,153,0.12), transparent 60%)",
          }}
        />

        <div className="relative flex items-start justify-between gap-4 border-b border-border-1 p-6">
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-1 to-brand-3 font-display text-base font-bold text-white"
              aria-hidden
            >
              {(username ?? "?").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-text-3">
                {rank !== null && (
                  <span className="rounded-full bg-white/10 px-2 py-0.5 font-bold text-text-1">
                    {medalLabel(rank)}
                  </span>
                )}
                {team && <span>{team}</span>}
              </div>
              <div className="font-display text-2xl font-semibold text-text-1">
                {username}
              </div>
              <div className="tabular text-sm text-text-2">
                <span className="font-display text-lg font-bold text-text-1">
                  {fmt(totalScore)}
                </span>{" "}
                <span className="text-text-4">puntos totales</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-text-3 transition-colors hover:bg-white/10 hover:text-text-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-1"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative max-h-[calc(86vh-112px)] overflow-y-auto p-6">
          <Section title="Medallas conseguidas" count={earned.length}>
            {earned.length === 0 ? (
              <EmptyNote text="Aún no hay medallas conseguidas." />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {earned.map((m) => (
                  <EarnedMedal key={m.code} medal={m} />
                ))}
              </div>
            )}
          </Section>

          <div className="h-6" />

          <Section title="Por desbloquear" count={locked.length}>
            {locked.length === 0 ? (
              <EmptyNote text="¡Todas desbloqueadas! 🎉" />
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {locked.map((m) => (
                  <LockedMedal key={m.code} medal={m} />
                ))}
              </div>
            )}
          </Section>
        </div>
      </motion.div>
    </motion.div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section>
      <header className="mb-3 flex items-baseline justify-between">
        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-text-2">
          {title}
        </h3>
        <span className="tabular text-xs text-text-4">{count}</span>
      </header>
      {children}
    </section>
  );
}

function EmptyNote({ text }: { text: string }) {
  return (
    <div className="rounded-[14px] border border-dashed border-border-1 bg-white/[0.02] px-4 py-6 text-center text-xs text-text-3">
      {text}
    </div>
  );
}

function EarnedMedal({ medal }: { medal: MedalItem }) {
  return (
    <div className="flex items-start gap-3 rounded-[14px] border border-border-1 bg-white/[0.04] p-3">
      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gold-2/40 to-gold-2/5 text-2xl shadow-[0_0_18px_-4px_rgba(251,191,36,0.55)]">
        {medal.emoji}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="truncate font-semibold text-text-1">{medal.label}</div>
          <div className="text-[10px] uppercase tracking-wider text-gold-2">
            Conseguida
          </div>
        </div>
        <div className="mt-0.5 line-clamp-2 text-xs text-text-3">{medal.desc}</div>
        {medal.earned_at && (
          <div className="mt-1 text-[11px] text-text-4">
            {dateSpanish(medal.earned_at)}
          </div>
        )}
      </div>
    </div>
  );
}

function LockedMedal({ medal }: { medal: MedalItem }) {
  const pct = Math.max(0, Math.min(100, medal.pct));
  const missing = Math.max(0, medal.target - medal.current);
  return (
    <div className="flex flex-col gap-2 rounded-[14px] border border-border-1 bg-white/[0.02] p-3 opacity-90">
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-white/[0.04] text-2xl grayscale opacity-70">
          {medal.emoji}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold text-text-2">{medal.label}</div>
          <div className="mt-0.5 line-clamp-2 text-xs text-text-3">{medal.desc}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 text-[11px] text-text-3">
        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full bg-gradient-to-r from-brand-1 to-brand-3"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span className="tabular font-semibold text-text-2">{pct.toFixed(0)}%</span>
      </div>
      <div className="flex items-center justify-between text-[11px] text-text-4">
        <span className="tabular">
          {fmt(medal.current)} / {fmt(medal.target)} {medal.unit}
        </span>
        {missing > 0 && (
          <span className="text-brand-2">faltan {fmt(missing)} {medal.unit}</span>
        )}
      </div>
    </div>
  );
}
